import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../config/firebase'

const functions = getFunctions(app)

// Admin session management
let adminSessionToken: string | null = null
let sessionExpiresAt: number | null = null

export const secureAdminService = {
  // Login to admin system
  async login(username: string, password: string): Promise<{ success: boolean; sessionToken: string; expiresAt: number }> {
    try {
      const adminLogin = httpsCallable(functions, 'adminLogin')
      
      // Get client IP for rate limiting (in production, this should be more sophisticated)
      const clientIP = 'unknown' // In a real app, you'd get this from the server
      
      const result = await adminLogin({ username, password, clientIP })
      const data = result.data as any
      
      if (data.success) {
        adminSessionToken = data.sessionToken
        sessionExpiresAt = data.expiresAt
        
        // Store session token in sessionStorage for persistence
        sessionStorage.setItem('adminSessionToken', data.sessionToken)
        sessionStorage.setItem('adminSessionExpiresAt', data.expiresAt.toString())
        
        return data
      } else {
        throw new Error('Login failed')
      }
    } catch (error: any) {
      console.error('Admin login error:', error)
      throw new Error(error.message || 'Login failed')
    }
  },

  // Validate current session
  async validateSession(): Promise<boolean> {
    try {
      // Check if we have a stored session
      const storedToken = sessionStorage.getItem('adminSessionToken')
      const storedExpiresAt = sessionStorage.getItem('adminSessionExpiresAt')
      
      if (!storedToken || !storedExpiresAt) {
        return false
      }
      
      const expiresAt = parseInt(storedExpiresAt)
      if (Date.now() > expiresAt) {
        // Session expired, clean up
        this.logout()
        return false
      }
      
      // Update current session
      adminSessionToken = storedToken
      sessionExpiresAt = expiresAt
      
      // Validate with server
      const validateSession = httpsCallable(functions, 'validateAdminSession')
      const result = await validateSession({ sessionToken: storedToken })
      const data = result.data as any
      
      return data.valid
    } catch (error) {
      console.error('Session validation error:', error)
      this.logout()
      return false
    }
  },

  // Logout from admin system
  async logout(): Promise<void> {
    try {
      if (adminSessionToken) {
        const adminLogout = httpsCallable(functions, 'adminLogout')
        await adminLogout({ sessionToken: adminSessionToken })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clean up local session
      adminSessionToken = null
      sessionExpiresAt = null
      sessionStorage.removeItem('adminSessionToken')
      sessionStorage.removeItem('adminSessionExpiresAt')
    }
  },

  // Get admin data securely
  async getAdminData(dataType: 'adminSettings' | 'pricingPlans' | 'subscriptions' | 'users' | 'members' | 'promoCodes'): Promise<any> {
    try {
      console.log(`Getting admin data for type: ${dataType}`)
      
      // Ensure we have a valid session
      if (!adminSessionToken) {
        console.log('No admin session token, validating session...')
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      console.log('Calling Firebase function getAdminData...')
      const getAdminData = httpsCallable(functions, 'getAdminData')
      const result = await getAdminData({ sessionToken: adminSessionToken, dataType })
      const data = result.data as any
      
      console.log('Firebase function response:', data)
      
      if (data.success) {
        console.log(`Successfully retrieved ${dataType} data:`, data.data)
        return data.data
      } else {
        console.error('Firebase function returned error:', data)
        throw new Error(data.error || 'Failed to retrieve data')
      }
    } catch (error: any) {
      console.error('Admin data retrieval error:', error)
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack
      })
      throw new Error(error.message || 'Data retrieval failed')
    }
  },

  // Get admin settings
  async getAdminSettings() {
    return this.getAdminData('adminSettings')
  },

  // Get pricing plans
  async getPricingPlans() {
    return this.getAdminData('pricingPlans')
  },

  // Get all subscriptions
  async getAllSubscriptions() {
    return this.getAdminData('subscriptions')
  },

  // Get all users
  async getAllUsers() {
    return this.getAdminData('users')
  },

  // Get members (users with subscription data)
  async getMembers() {
    return this.getAdminData('members')
  },

  // Get promo codes
  async getPromoCodes() {
    return this.getAdminData('promoCodes')
  },

  // Create pricing plan
  async createPricingPlan(planData: any): Promise<string> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const createPricingPlan = httpsCallable(functions, 'createPricingPlan')
      const result = await createPricingPlan({ sessionToken: adminSessionToken, planData })
      const data = result.data as any
      
      if (data.success) {
        return data.planId
      } else {
        throw new Error('Failed to create pricing plan')
      }
    } catch (error: any) {
      console.error('Create pricing plan error:', error)
      throw new Error(error.message || 'Failed to create pricing plan')
    }
  },

  // Update pricing plan
  async updatePricingPlan(planId: string, planData: any): Promise<void> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const updatePricingPlan = httpsCallable(functions, 'updatePricingPlan')
      const result = await updatePricingPlan({ sessionToken: adminSessionToken, planId, planData })
      const data = result.data as any
      
      if (!data.success) {
        throw new Error('Failed to update pricing plan')
      }
    } catch (error: any) {
      console.error('Update pricing plan error:', error)
      throw new Error(error.message || 'Failed to update pricing plan')
    }
  },

  // Delete pricing plan
  async deletePricingPlan(planId: string): Promise<void> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const deletePricingPlan = httpsCallable(functions, 'deletePricingPlan')
      const result = await deletePricingPlan({ sessionToken: adminSessionToken, planId })
      const data = result.data as any
      
      if (!data.success) {
        throw new Error('Failed to delete pricing plan')
      }
    } catch (error: any) {
      console.error('Delete pricing plan error:', error)
      throw new Error(error.message || 'Failed to delete pricing plan')
    }
  },

  // Update admin settings
  async updateAdminSettings(settings: any): Promise<void> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const updateAdminSettings = httpsCallable(functions, 'updateAdminSettings')
      const result = await updateAdminSettings({ sessionToken: adminSessionToken, settings })
      const data = result.data as any
      
      if (!data.success) {
        throw new Error('Failed to update admin settings')
      }
    } catch (error: any) {
      console.error('Update admin settings error:', error)
      throw new Error(error.message || 'Failed to update admin settings')
    }
  },

  // Check if user is currently authenticated as admin
  isAuthenticated(): boolean {
    return adminSessionToken !== null && sessionExpiresAt !== null && Date.now() < sessionExpiresAt
  },

  // Get current session info
  getSessionInfo() {
    return {
      isAuthenticated: this.isAuthenticated(),
      expiresAt: sessionExpiresAt
    }
  },

  // Create promo code
  async createPromoCode(promoData: any): Promise<string> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const createPromoCode = httpsCallable(functions, 'createPromoCode')
      const result = await createPromoCode({ sessionToken: adminSessionToken, promoData })
      const data = result.data as any
      
      if (data.success) {
        return data.promoId
      } else {
        throw new Error('Failed to create promo code')
      }
    } catch (error: any) {
      console.error('Create promo code error:', error)
      throw new Error(error.message || 'Failed to create promo code')
    }
  },

  // Update promo code
  async updatePromoCode(promoId: string, promoData: any): Promise<void> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const updatePromoCode = httpsCallable(functions, 'updatePromoCode')
      const result = await updatePromoCode({ sessionToken: adminSessionToken, promoId, promoData })
      const data = result.data as any
      
      if (!data.success) {
        throw new Error('Failed to update promo code')
      }
    } catch (error: any) {
      console.error('Update promo code error:', error)
      throw new Error(error.message || 'Failed to update promo code')
    }
  },

  // Delete promo code
  async deletePromoCode(promoId: string): Promise<void> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const deletePromoCode = httpsCallable(functions, 'deletePromoCode')
      const result = await deletePromoCode({ sessionToken: adminSessionToken, promoId })
      const data = result.data as any
      
      if (!data.success) {
        throw new Error('Failed to delete promo code')
      }
    } catch (error: any) {
      console.error('Delete promo code error:', error)
      throw new Error(error.message || 'Failed to delete promo code')
    }
  },

  // Delete user
  async deleteUser(userId: string): Promise<void> {
    try {
      if (!adminSessionToken) {
        const isValid = await this.validateSession()
        if (!isValid) {
          throw new Error('No valid admin session')
        }
      }
      
      const deleteUser = httpsCallable(functions, 'deleteUser')
      const result = await deleteUser({ sessionToken: adminSessionToken, userId })
      const data = result.data as any
      
      if (!data.success) {
        throw new Error('Failed to delete user')
      }
    } catch (error: any) {
      console.error('Delete user error:', error)
      throw new Error(error.message || 'Failed to delete user')
    }
  }
} 